import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Play, MapPin, HardHat, MonitorSmartphone, Link2 } from "lucide-react";
import { TRIAL_CTA, SUPPORT_EMAIL, SIGNUP_REASSURANCE } from "@/data/marketing";
import { STAGES, INDUSTRIES, GROUPS } from "./WebsiteSections";
import { SCENE, Laptop, Phone, Glass, Rows, Checks, Meter } from "./CinematicParts";
import "./cinematic-sections.css";

// Cinematic preview sections. All copy is taken from the existing homepage;
// floating panels are illustrative and use demonstration records.
const PLAY_URL = "https://play.google.com/store/apps/details?id=nz.co.fieldlink.app";
const STEPS = ["Takeoff", "Estimate", "Quote", "Job", "Crew & Plant", "Field", "Safety & QA", "Cost", "Invoice"];
const group = (name) => GROUPS.find((g) => g[0] === name);
const st = (i) => [STAGES[i][0], STAGES[i][1], STAGES[i][4]];

const Eyebrow = ({ children }) => <p className="fx-eyebrow">{children}</p>;

export function ConnectedPlatform() {
  return (
    <section className="fx-connected" id="workflow" aria-labelledby="fx-connected-title" data-testid="fx-connected">
      <div className="fx-transition" aria-hidden="true" />
      <div className="fx-wrap">
        <div className="fx-strip fx-reveal" data-testid="fx-trust-strip">
          <span>Built in New Zealand</span><span>Built for civil</span><span>Office + field</span>
          <Link to="/xero-timesheet-integration-nz">Xero connected ↗</Link>
          <a className="fx-play" href={PLAY_URL} target="_blank" rel="noopener noreferrer" data-testid="fx-google-play"><Play size={14} fill="currentColor" /> Get the Android app on Google Play</a>
        </div>
        <div className="fx-connected-head fx-reveal">
          <Eyebrow>Everything follows the Job</Eyebrow>
          <h2 id="fx-connected-title" className="fx-h2">One platform.<br />Every job.<br /><em>Every crew.</em></h2>
          <div>
            <p className="fx-lead"><strong>One Job. One system.</strong> Price the work. Put the crew on site. Keep the records and costs together. Fieldlink connects the steps that usually live in separate spreadsheets, inboxes and apps.</p>
          </div>
        </div>
        <ConnectedStage />
        <ol className="fx-steps fx-reveal" aria-label="The Fieldlink workflow">
          {STEPS.map((s, i) => <li key={s}><span>{String(i + 1).padStart(2, "0")}</span>{s}</li>)}
        </ol>
      </div>
    </section>
  );
}

function ConnectedStage() {
  return (
    <div className="fx-stage fx-reveal" data-testid="fx-connected-stage">
      <div className="fx-stage-glow" aria-hidden="true" />
      <Laptop screen="job" caption="Job overview" className="fx-stage-laptop" />
      <Glass title="Jobs" meta="3 active" className="fx-p fx-p1" testid="fx-panel-jobs">
        <Rows rows={[["Subdivision Stage 1", "In progress", "hot"], ["Drainage Works", "In progress", "hot"], ["Roading Upgrade", "Scheduled"]]} />
      </Glass>
      <Glass title="Timesheets" meta="This week" className="fx-p fx-p2" testid="fx-panel-timesheets">
        <Meter label="Blake S." value="48 hrs" pct={92} /><Meter label="Cooper M." value="44 hrs" pct={80} />
      </Glass>
      <Glass title="Crew & Plant" meta="On site" className="fx-p fx-p3" testid="fx-panel-plant">
        <Rows rows={[["20t Excavator", "Allocated", "ok"], ["6-wheeler", "Allocated", "ok"], ["Roller", "Service due", "hot"]]} />
      </Glass>
      <Glass title="Health & Safety" meta="Today" className="fx-p fx-p4" testid="fx-panel-safety">
        <Checks items={[["Daily pre-start", true], ["Toolbox talk", true], ["Site inspection", false]]} />
      </Glass>
      <Glass title="Job Costing" meta="vs budget" className="fx-p fx-p5" testid="fx-panel-costing">
        <Meter label="Labour + Plant" value="72%" pct={72} />
      </Glass>
      <Glass title="Invoicing" meta="Xero" className="fx-p fx-p6" testid="fx-panel-invoicing">
        <Rows rows={[["Progress claim 3", "Draft to Xero", "ok"]]} />
      </Glass>
      <p className="fx-note">Actual Fieldlink screen. Floating panels are illustrative, using demonstration records.</p>
    </div>
  );
}

const SHOWCASES = [
  { id: "jobs", label: "Jobs & Projects", title: <>Win it.<br /><em>Plan it.</em></>, scene: "jobs",
    blocks: [st(0), st(1)], cta: ["Explore job management", "/job-management-nz"], screen: "job", caption: "Job overview",
    panel: <Glass title="Subdivision Stage 1" meta="FL-1024"><Rows rows={[["Quote", "Accepted", "ok"], ["Crew", "Allocated", "ok"], ["Stage", "In progress", "hot"]]} /></Glass> },
  { id: "timesheets", label: "Timesheets & Payroll", title: <>Build it.<br /><em>Pay it right.</em></>, scene: "timesheets", phone: true,
    blocks: [st(2), [group("People")[1], group("People")[2], group("People")[4]]], cta: ["Explore timesheets", "/timesheet-app-nz"],
    panel: <Glass title="Timesheets" meta="Approved"><Meter label="Blake S." value="48 hrs" pct={92} /><Meter label="Ryan K." value="41 hrs" pct={74} /></Glass> },
  { id: "plant", label: "Crew & Plant", title: <>Know what is<br /><em>allocated.</em></>, scene: "plant",
    blocks: [[group("Operations")[1], group("Operations")[2], group("Operations")[4]], [group("Plant")[1], group("Plant")[2], group("Plant")[4]]],
    cta: ["Explore Plant", "/fleet-management-nz"], screen: "work", caption: "Job tasks and site records",
    panel: <Glass title="Plant" meta="Today"><Rows rows={[["20t Excavator", "On site", "ok"], ["Articulated truck", "On site", "ok"], ["Roller", "Service due", "hot"]]} /></Glass> },
  { id: "safety", label: "Health & Safety", title: <>Keep it<br /><em>safe.</em></>, scene: "safety",
    blocks: [st(3), [group("Safety & QA")[1], group("Safety & QA")[2], group("Safety & QA")[4]]], cta: ["Explore safety", "/safety-management-nz"],
    screen: "safety", caption: "Job Safety and assigned forms",
    panel: <Glass title="Pre-start checklist" meta="Today"><Checks items={[["Site hazards reviewed", true], ["Plant checked", true], ["Crew signed on", false]]} /></Glass> },
  { id: "costs", label: "Job Costing & Invoicing", title: <>Know it.<br /><em>Get paid.</em></>, scene: "finance",
    blocks: [st(4), st(5)], cta: ["Explore job costing", "/civil-job-costing-software-nz"], screen: "costs", caption: "Job costs and materials",
    panel: <Glass title="Job cost" meta="vs budget"><Meter label="Recorded spend" value="72%" pct={72} /><Rows rows={[["Progress claim", "Draft to Xero", "ok"]]} /></Glass> },
];

export function FeatureShowcases() {
  return (
    <section className="fx-showcases" id="product" aria-labelledby="fx-showcases-title" data-testid="fx-showcases">
      <div className="fx-wrap">
        <div className="fx-section-head fx-reveal">
          <Eyebrow>From first measure to final invoice</Eyebrow>
          <h2 id="fx-showcases-title" className="fx-h2">The whole Job.<br /><em>Right in front of you.</em></h2>
        </div>
        {SHOWCASES.map((s, i) => <Showcase key={s.id} s={s} flip={i % 2 === 1} n={i + 1} />)}
      </div>
    </section>
  );
}

function Showcase({ s, flip, n }) {
  const scene = SCENE(s.scene);
  return (
    <article className={`fx-show ${flip ? "is-flip" : ""}`} data-testid={`fx-show-${s.id}`}>
      <div className="fx-show-copy fx-reveal">
        <Eyebrow>{String(n).padStart(2, "0")} · {s.label}</Eyebrow>
        <h3 className="fx-h3">{s.title}</h3>
        {s.blocks.map(([t, text, href]) => (
          <div className="fx-show-block" key={t}><b>{t}</b><p>{text}</p>{href && <Link to={href}>Explore the workflow <ArrowRight size={15} /></Link>}</div>
        ))}
        <Link className="fx-btn fx-btn-primary" to={s.cta[1]} data-testid={`fx-show-${s.id}-cta`}>{s.cta[0]} <ArrowRight size={17} /></Link>
      </div>
      <div className="fx-show-visual fx-reveal">
        <div className="fx-scene"><img src={scene.src} srcSet={scene.srcSet} sizes="(max-width: 900px) 92vw, 640px" loading="lazy" decoding="async" width="1264" height="848" alt="" /></div>
        {s.phone ? <Phone className="fx-show-phone" /> : <Laptop screen={s.screen} caption={s.caption} className="fx-show-laptop" />}
        <div className="fx-show-panel">{s.panel}</div>
      </div>
    </article>
  );
}

export function ProductExperience() {
  const [sel, setSel] = useState(0);
  const g = GROUPS[sel];
  const caption = STAGES.find((st) => st[2] === g[3])?.[3] || "Job work view";
  return (
    <section className="fx-experience" aria-labelledby="fx-why-title" data-testid="fx-experience">
      <div className="fx-wrap">
        <div className="fx-exp-top" id="why">
          <div className="fx-reveal">
            <Eyebrow>One connected working day</Eyebrow>
            <h2 id="fx-why-title" className="fx-h2">The office knows.<br /><em>The crew knows.</em></h2>
            <p className="fx-lead">The office plans the Job. The crew opens Fieldlink and sees the work that belongs to them.</p>
            <ul className="fx-checks">{["Job and crew allocations", "Allocated Plant and daily instructions", "Required forms and safety records", "Timesheets and site records"].map((t) => <li key={t}><Check size={17} />{t}</li>)}</ul>
            <Link className="fx-btn fx-btn-ghost" to="/download" data-testid="fx-explore-mobile">Explore Fieldlink on mobile <ArrowRight size={17} /></Link>
          </div>
          <div className="fx-devices fx-reveal">
            <div className="fx-stage-glow" aria-hidden="true" />
            <Laptop screen="work" caption="Office Job work view" className="fx-devices-laptop" />
            <Phone caption="Mobile Job work view" className="fx-devices-phone" />
          </div>
        </div>
        <div className="fx-explorer fx-reveal" id="explorer">
          <Eyebrow>Room to run the whole business</Eyebrow>
          <h2 className="fx-h2 fx-h2-sm">Depth where you need it.</h2>
          <div className="fx-tabs" role="group" aria-label="Product capabilities">
            {GROUPS.map((x, i) => <button key={x[0]} type="button" aria-pressed={sel === i} onClick={() => setSel(i)} data-testid={`fx-tab-${x[0].toLowerCase().replace(/[^a-z]+/g, "-")}`}>{x[0]}</button>)}
          </div>
          <div className="fx-explorer-body" aria-live="polite">
            <div className="fx-glass fx-explorer-copy">
              <h3 className="fx-h3 fx-h3-sm">{g[1]}</h3><p>{g[2]}</p>
              <Link className="fx-link" to={g[4]} data-testid="fx-explorer-link">Explore {g[0]} <ArrowRight size={16} /></Link>
            </div>
            <Laptop key={g[3]} screen={g[3]} caption={caption} className="fx-explorer-laptop" />
          </div>
        </div>
      </div>
    </section>
  );
}

const WHY = [
  [MapPin, "Built in New Zealand", "Civil construction software built in New Zealand, with NZ support.", null],
  [HardHat, "Built for civil", "From tender to timesheet. Crew to plant. Site to invoice.", null],
  [MonitorSmartphone, "Office + field", "The office plans the Job. The crew sees the work that belongs to them.", "/download"],
  [Link2, "Xero connected", "Send customer invoices to Xero as drafts and export approved timesheets for payroll.", "/xero-timesheet-integration-nz"],
];

export function WhyFieldlink() {
  return (
    <section className="fx-why" id="industries" aria-labelledby="fx-industries-title" data-testid="fx-why">
      <div className="fx-wrap">
        <div className="fx-why-cards">
          {WHY.map(([Icon, t, d, to]) => {
            const body = <><span className="fx-why-ic"><Icon size={22} /></span><b>{t}</b><p>{d}</p></>;
            return to ? <Link key={t} to={to} className="fx-glass fx-why-card fx-reveal">{body}</Link> : <div key={t} className="fx-glass fx-why-card fx-reveal">{body}</div>;
          })}
        </div>
        <div className="fx-section-head fx-reveal">
          <Eyebrow>For the work you do</Eyebrow>
          <h2 id="fx-industries-title" className="fx-h2">Built for civil.<br /><em>Down to the details.</em></h2>
        </div>
        <div className="fx-industries">
          {INDUSTRIES.map(([name, text, img]) => (
            <article key={name} className="fx-industry fx-reveal">
              <img src={`/marketing/${img}.jpg`} loading="lazy" decoding="async" width="640" height="440" alt={`${name} construction work`} />
              <div><h3>{name}</h3><p>{text}</p></div>
            </article>
          ))}
        </div>
        <div className="fx-xero fx-glass fx-reveal">
          <div><Eyebrow>Connected to your accounts</Eyebrow><h2 className="fx-h2 fx-h2-sm">Keep Xero in the picture.</h2></div>
          <div><p>Send customer invoices to Xero as drafts and export approved timesheets for payroll. Keep your accounts workflow connected to the work on site.</p>
            <Link className="fx-link" to="/xero-timesheet-integration-nz">See how Xero connects <ArrowRight size={17} /></Link></div>
        </div>
      </div>
    </section>
  );
}

export function FinalCinematicCTA() {
  const bg = SCENE("final");
  return (
    <section className="fx-final" aria-labelledby="fx-final-title" data-testid="fx-final-cta">
      <img className="fx-final-bg" src={bg.src} srcSet={bg.srcSet} sizes="100vw" loading="lazy" decoding="async" alt="" />
      <div className="fx-wrap fx-final-inner fx-reveal">
        <Eyebrow>Built for the work ahead</Eyebrow>
        <h2 id="fx-final-title" className="fx-h2 fx-h2-xl">Ready to take control of your <em>civil business?</em></h2>
        <p className="fx-lead">Less paperwork. More mahi. Run your civil business from one connected platform.</p>
        <div className="fx-actions">
          <Link className="fx-btn fx-btn-primary fx-btn-lg" to="/register" data-testid="fx-final-trial">{TRIAL_CTA} <ArrowRight size={18} /></Link>
          <a className="fx-btn fx-btn-ghost fx-btn-lg" href={`mailto:${SUPPORT_EMAIL}?subject=Fieldlink%20demo%20request`} data-testid="fx-final-demo">Book a demo</a>
        </div>
        <p className="fx-fine">No credit card · 7 days free · NZ support</p>
        <p className="fx-fine">{SIGNUP_REASSURANCE}</p>
      </div>
    </section>
  );
}
